import {
  $createParagraphNode,
  $getNodeByKey,
  $copyNode,
  $getRoot,
  $createTextNode,
} from 'lexical';
import { $createHeadingNode } from '@lexical/rich-text';
import { $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createCalloutNode } from '../../LexicalEditor/nodes/CalloutNode';
import { $createToggleNode } from '../../LexicalEditor/nodes/ToggleNode';

/**
 * Replace a top-level block node with a new node, transferring all children.
 * Works directly by nodeKey — no selection dependency.
 */
function replaceBlockWithNode(editor, nodeKey, createNewNode) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;

    const newNode = createNewNode();
    const children = node.getChildren();
    for (const child of children) {
      newNode.append(child);
    }
    node.replace(newNode);
    newNode.selectStart();
  });
}

export function turnIntoParagraph(editor, nodeKey) {
  replaceBlockWithNode(editor, nodeKey, () => $createParagraphNode());
}

export function turnIntoHeading(editor, nodeKey, tag) {
  replaceBlockWithNode(editor, nodeKey, () => $createHeadingNode(tag));
}

export function turnIntoBulletList(editor, nodeKey, currentBlockType) {
  // Set selection on the node first, then dispatch the list command
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (node) node.selectStart();
  });
  if (currentBlockType !== 'ul') {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  } else {
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
  }
}

export function turnIntoNumberedList(editor, nodeKey, currentBlockType) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (node) node.selectStart();
  });
  if (currentBlockType !== 'ol') {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  } else {
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
  }
}

export function turnIntoQuote(editor, nodeKey) {
  replaceBlockWithNode(editor, nodeKey, () => $createQuoteNode());
}

export function turnIntoCode(editor, nodeKey) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;

    const textContent = node.getTextContent();
    const codeNode = $createCodeNode();
    if (textContent) {
      codeNode.append($createTextNode(textContent));
    }
    node.replace(codeNode);
    codeNode.selectStart();
  });
}

export function turnIntoCallout(editor, nodeKey, calloutType = 'info') {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;

    const calloutNode = $createCalloutNode(calloutType);
    const paragraph = $createParagraphNode();
    calloutNode.append(paragraph);

    const textContent = node.getTextContent();
    node.replace(calloutNode);

    if (textContent) {
      paragraph.append($createTextNode(textContent));
    }
    paragraph.select();
  });
}

export function turnIntoToggle(editor, nodeKey) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;

    const toggleNode = $createToggleNode();
    const paragraph = $createParagraphNode();
    toggleNode.append(paragraph);

    const textContent = node.getTextContent();
    node.replace(toggleNode);

    if (textContent) {
      paragraph.append($createTextNode(textContent));
    }
    paragraph.select();
  });
}

export function duplicateBlock(editor, nodeKey) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;
    const clone = $copyNode(node);
    node.insertAfter(clone);
  });
}

export function deleteBlock(editor, nodeKey) {
  editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;
    const root = $getRoot();
    // If this is the last child, replace with empty paragraph instead of leaving empty doc
    if (root.getChildrenSize() <= 1) {
      const p = $createParagraphNode();
      node.insertAfter(p);
      node.remove();
      p.select();
    } else {
      // Select the previous or next sibling before removing
      const prev = node.getPreviousSibling();
      const next = node.getNextSibling();
      node.remove();
      if (prev) {
        prev.selectEnd();
      } else if (next) {
        next.selectStart();
      }
    }
  });
}

export function copyBlockContent(editor, nodeKey) {
  let text = '';
  editor.read(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node) return;
    text = node.getTextContent();
  });
  if (text) {
    navigator.clipboard.writeText(text);
  }
}
